import { useRouter } from 'next/router'
import styled from 'styled-components'

import { theme } from '@/styles/theme'
import { formatDate } from '@/utils/date'

import { HTMLParser } from '@/components/elements/HTMLParser'
import { NewsTagButton } from '@/components/elements/News/NewsTagButton'
import { PDFLink } from '@/components/elements/PDFLink'
import { Spacer } from '@/components/elements/Spacer'

import type { NewsRelease } from '@/types/newsRelease'

export const NewsArticle = ({
  tag,
  title,
  body,
  pdf,
  publishDate,
}: NewsRelease) => {
  const router = useRouter()

  const handleSearchTag = (tag: string) => {
    router.push({ pathname: '/news/release', query: { tag } }, '/news/release')
  }

  return (
    <Article>
      <header>
        <HeaderMeta>
          <div>
            <TagList>
              {tag.map((tag) => (
                <NewsTagButton
                  tag={tag}
                  key={tag.name}
                  size="small"
                  isActive={false}
                  onClick={() => handleSearchTag(tag.name)}
                />
              ))}
            </TagList>
            <Date dateTime={publishDate}>
              {formatDate(publishDate, 'yyyy年MM月dd日')}
            </Date>
          </div>
          {pdf && <PDFLink url={pdf.src} />}
        </HeaderMeta>
        <Spacer h={{ sp: 30, pc: 60 }} />
        <Title>{title}</Title>
      </header>
      <Spacer h={{ sp: 50, pc: 70 }} />
      <Section>
        <HTMLParser text={body} />
      </Section>
    </Article>
  )
}

const HeaderMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    justify-content: initial;
    align-items: flex-start;
    gap: 26px;
  }
`

const Article = styled.article`
  width: 1040px;
  margin: 74px auto 130px;

  @media screen and (max-width: 1047px) {
    width: 100%;
    margin: 40px auto 80px;
    padding: 0 20px;
  }
`

const TagList = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;

  @media screen and (max-width: 1047px) {
    gap: 11px;
  }
`

const Date = styled.time`
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0;
  color: ${theme.colors.textTitle};

  @media screen and (max-width: 1047px) {
    font-size: 14px;
    line-height: 24px;
  }
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: bold;
  letter-spacing: 1.68px;
  line-height: 42px;
  color: ${theme.colors.trialBlue};

  @media screen and (max-width: 1047px) {
    font-size: 24px;
    letter-spacing: 0.96px;
    line-height: 36px;
  }
`

const Section = styled.section`
  p {
    font-size: 18px;
    letter-spacing: 0.36px;
    line-height: 34px;
    color: ${theme.colors.textTitle};
    margin-bottom: 20px;
  }

  a {
    color: ${theme.colors.trialBlue};

    &:hover {
      text-decoration: underline;
    }

    > svg {
      margin-left: 8px;
    }
  }

  strong {
    font-weight: bold;
  }

  em {
    font-style: italic;
  }

  img {
    max-width: 100%;
  }

  h2 {
    font-size: 24px;
    font-weight: bold;
    letter-spacing: 1.44px;
    line-height: 42px;
    color: ${theme.colors.trialBlue};
    margin-top: 40px;
    margin-bottom: 20px;
  }

  h3 {
    font-size: 22px;
    font-weight: bold;
    letter-spacing: 1.32px;
    line-height: 42px;
    color: ${theme.colors.textTitle};
    margin-top: 40px;
    margin-bottom: 20px;
  }

  @media screen and (max-width: 1047px) {
    p {
      font-size: 15px;
      letter-spacing: 0.3px;
      line-height: 25px;
    }

    a {
      text-decoration: underline;
      word-break: break-all;
    }

    h2 {
      font-size: 22px;
      letter-spacing: 0.44px;
      line-height: 33px;
      margin-top: 29px;
      margin-bottom: 18px;
    }

    h3 {
      font-size: 20px;
      letter-spacing: 0.4px;
      line-height: 42px;
      margin-top: 29px;
      margin-bottom: 18px;
    }
  }
`
